import {
  collection,
  doc,
  documentId,
  getDoc,
  orderBy,
  query,
  where,
  type QueryConstraint,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { err, ok, type Result } from '@shared/result';
import { asId, type AreaId, type HolidayId, type PersonId, type SubAreaId } from '@shared/brand';
import type {
  ActivateAccountInput,
  MergePendingAccountInput,
  PendingAccountMatch,
  CreatedPerson,
  CreatePersonInput,
  TerminatePersonInput,
  TerminationResult,
  ReactivatePersonInput,
  ReactivationResult,
  BalanceAdjusted,
  BalanceAdjustment,
  PersonDetailPatch,
  PersonDetailUpdate,
  AddHolidayInput,
  CreateAreaInput,
  CreateSubAreaInput,
  BalanceRef,
  IBalanceRepository,
  IHolidayRepository,
  ILogger,
  IOrganizationRepository,
  IPersonLeaderRepository,
  IPersonRepository,
  Page,
  PageRequest,
  PersonScope,
  RepositoryError,
  RosterImportResult,
  UpdateSubAreaInput,
  TransferAreaInput,
  TransferAreaResult,
  AttachPersonDocumentInput,
  SetPersonPermissionsInput,
  SaveAreaCatalogInput,
} from '@application/ports';
import type { Balance } from '@domain/entities/Balance';
import type { Holiday } from '@domain/entities/Holiday';
import { enabledHolidayDates } from '@domain/entities/Holiday';
import type { Area, Company, SubArea } from '@domain/entities/Organization';
import type { Person, PersonLeaderLink } from '@domain/entities/Person';
import { personLeaderLinkId } from '@domain/entities/Person';
import type { Role } from '@domain/vocabulary';
import { COLLECTIONS, firebase } from '../firebase/client';
import {
  decodeArea,
  decodeBalance,
  decodeCompany,
  decodeHoliday,
  decodePerson,
  decodePersonLeaderLink,
  decodeSubArea,
} from '../codecs/entities';
import { safeDecode } from '../codecs/base';
import {
  chunk,
  collectAllPages,
  isUnconfirmedEmpty,
  IN_QUERY_CHUNK,
  runCountQuery,
  runPagedQuery,
  translateCallableError,
  translateFirestoreError,
} from './support';

/**
 * The read-side adapters for the organisation: company, areas, sub areas, people,
 * the leader junction, holidays and balances.
 *
 * Every mutation goes through a callable, because each one carries a consequence
 * the client cannot be trusted with: activating an account mints custom claims,
 * assigning a leader changes who may approve, editing a sub area's cap changes the
 * scheduling constraint for everyone in it. The security rules deny direct client
 * writes to all of these.
 */

const notFoundError = (what: string): RepositoryError => ({ kind: 'not-found', what });

const callable = async <Req, Res>(name: string, payload: Req): Promise<Result<Res, RepositoryError>> => {
  try {
    const { functions } = firebase();
    const invoke = httpsCallable<Req, Res>(functions, name);
    const response = await invoke(payload);
    return ok(response.data);
  } catch (cause) {
    return err(translateCallableError(cause, name));
  }
};

/** Reads one document and decodes it, or reports not-found. */
const readOne = async <T>(
  collectionName: string,
  docId: string,
  decode: (id: string, data: Record<string, unknown>) => T | null,
): Promise<Result<T, RepositoryError>> => {
  try {
    const { db } = firebase();
    const snapshot = await getDoc(doc(db, collectionName, docId));
    if (!snapshot.exists()) return err(notFoundError(`${collectionName}/${docId}`));

    const decoded = decode(snapshot.id, snapshot.data());
    if (decoded === null) {
      return err({
        kind: 'malformed-document',
        path: `${collectionName}/${docId}`,
        detail: 'required field missing or invalid',
      });
    }
    return ok(decoded);
  } catch (cause) {
    return err(translateFirestoreError(cause, `${collectionName}/${docId}`));
  }
};

// ---------------------------------------------------------------------------
// Organisation
// ---------------------------------------------------------------------------

export class FirestoreOrganizationRepository implements IOrganizationRepository {
  constructor(private readonly logger: ILogger) {}

  getCompany(): Promise<Result<Company, RepositoryError>> {
    return readOne(COLLECTIONS.companies, firebase().env.companyId, decodeCompany);
  }

  async listAreas(): Promise<Result<readonly Area[], RepositoryError>> {
    // Bounded by nature: a company has a handful of areas, not thousands.
    return collectAllPages((page) =>
      runPagedQuery(
        collection(firebase().db, COLLECTIONS.areas),
        [orderBy('name')],
        page,
        decodeArea,
        'areas',
        (failures) => this.logger.warn('undecodable area documents', { failures }),
      ),
    );
  }

  async listSubAreas(areaId: AreaId): Promise<Result<readonly SubArea[], RepositoryError>> {
    return collectAllPages((page) =>
      runPagedQuery(
        collection(firebase().db, COLLECTIONS.subAreas),
        [where('areaId', '==', areaId), orderBy('name')],
        page,
        decodeSubArea,
        'subAreas',
        (failures) => this.logger.warn('undecodable subArea documents', { failures }),
      ),
    );
  }

  getSubArea(subAreaId: SubAreaId): Promise<Result<SubArea, RepositoryError>> {
    return readOne(COLLECTIONS.subAreas, subAreaId, decodeSubArea);
  }

  async createSubArea(input: CreateSubAreaInput): Promise<Result<SubAreaId, RepositoryError>> {
    const result = await callable<CreateSubAreaInput, { subAreaId: string }>(
      'createSubArea',
      input,
    );
    return result.ok ? ok(result.value.subAreaId as SubAreaId) : err(result.error);
  }

  updateSubArea(
    subAreaId: SubAreaId,
    patch: UpdateSubAreaInput,
  ): Promise<Result<void, RepositoryError>> {
    return callable<{ subAreaId: string; patch: UpdateSubAreaInput }, void>('updateSubArea', {
      subAreaId,
      patch,
    });
  }

  deleteSubArea(subAreaId: SubAreaId): Promise<Result<void, RepositoryError>> {
    return callable<{ subAreaId: string }, void>('deleteSubArea', { subAreaId });
  }

  setAreaAdmin(areaId: AreaId, personId: PersonId): Promise<Result<void, RepositoryError>> {
    return callable<{ areaId: string; personId: string }, void>('setAreaAdmin', {
      areaId,
      personId,
    });
  }


  setAreaActive(areaId: AreaId, active: boolean): Promise<Result<void, RepositoryError>> {
    return callable<{ areaId: string; active: boolean }, void>('setAreaActive', {
      areaId,
      active,
    });
  }

  async createArea(input: CreateAreaInput): Promise<Result<AreaId, RepositoryError>> {
    const result = await callable<CreateAreaInput, { areaId: string }>('createArea', input);
    return result.ok ? ok(result.value.areaId as AreaId) : err(result.error);
  }

  deleteArea(areaId: AreaId): Promise<Result<void, RepositoryError>> {
    return callable<{ areaId: string }, void>('deleteArea', { areaId });
  }

  /* Entero cada vez: la pantalla edita una lista de diferencias, y mandar sólo
     lo que cambió obligaría al servidor a deducir qué se quitó. */
  saveAreaCatalog(input: SaveAreaCatalogInput): Promise<Result<void, RepositoryError>> {
    return callable<SaveAreaCatalogInput, void>('updateAreaCatalog', input);
  }

  /**
   * Runs the one-off import of Tecnología's roster.
   *
   * The list itself lives in the callable, not here: it is a migration, and
   * thirty-six people's entitlements should not travel as a payload a client
   * could edit. This is only the trigger.
   */
  importTechnologyRoster(): Promise<Result<RosterImportResult, RepositoryError>> {
    return callable<Record<string, never>, RosterImportResult>('importTechnologyRoster', {});
  }
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export class FirestorePersonRepository implements IPersonRepository {
  constructor(private readonly logger: ILogger) {}

  getById(personId: PersonId): Promise<Result<Person, RepositoryError>> {
    return readOne(COLLECTIONS.people, personId, decodePerson);
  }

  async getByUid(uid: string): Promise<Result<Person, RepositoryError>> {
    return this.findOneBy('uid', uid);
  }

  async getByEmail(email: string): Promise<Result<Person, RepositoryError>> {
    return this.findOneBy('email', email.trim().toLowerCase());
  }

  private async findOneBy(
    field: 'uid' | 'email',
    value: string,
  ): Promise<Result<Person, RepositoryError>> {
    const page = await runPagedQuery(
      collection(firebase().db, COLLECTIONS.people),
      [where(field, '==', value)],
      { limit: 1 },
      decodePerson,
      `people by ${field}`,
    );
    if (!page.ok) return err(page.error);

    const found = page.value.items[0];
    return found === undefined ? err(notFoundError(`person ${field}=${value}`)) : ok(found);
  }

  list(scope: PersonScope, page: PageRequest): Promise<Result<Page<Person>, RepositoryError>> {
    const constraints: QueryConstraint[] = [where('areaId', '==', scope.areaId)];
    if (scope.subAreaId !== undefined && scope.subAreaId !== null) {
      constraints.push(where('subAreaId', '==', scope.subAreaId));
    }
    if (scope.status !== undefined) constraints.push(where('status', '==', scope.status));
    constraints.push(orderBy('displayName'));

    return runPagedQuery(
      collection(firebase().db, COLLECTIONS.people),
      constraints,
      page,
      decodePerson,
      'people',
      (failures) => this.logger.warn('undecodable person documents', { failures }),
    );
  }

  /**
   * Resolves several people at once.
   *
   * Firestore's `in` operator caps at 30 values, so a longer list is split into
   * batched queries run concurrently. The alternative — one `getDoc` per id — is
   * the N+1 read that makes a roster screen expensive at 500 people.
   */
  async getMany(ids: readonly PersonId[]): Promise<Result<readonly Person[], RepositoryError>> {
    if (ids.length === 0) return ok([]);

    const unique = [...new Set(ids)];
    const batches = chunk(unique, IN_QUERY_CHUNK);

    const results = await Promise.all(
      batches.map((batch) =>
        runPagedQuery(
          collection(firebase().db, COLLECTIONS.people),
          [where(documentId(), 'in', batch)],
          { limit: batch.length },
          decodePerson,
          'people batch',
        ),
      ),
    );

    const people: Person[] = [];
    for (const result of results) {
      if (!result.ok) return err(result.error);
      people.push(...result.value.items);
    }
    return ok(people);
  }

  countByStatus(
    areaId: AreaId,
    status: Person['status'],
  ): Promise<Result<number, RepositoryError>> {
    return runCountQuery(
      collection(firebase().db, COLLECTIONS.people),
      [where('areaId', '==', areaId), where('status', '==', status)],
      'people count',
    );
  }

  /** The one direct client write on `people`, and the rules restrict it to these
   *  fields on one's own document. */
  async updateNotificationPreferences(
    personId: PersonId,
    prefs: Person['notificationPrefs'],
  ): Promise<Result<void, RepositoryError>> {
    try {
      const { db } = firebase();
      const { updateDoc, serverTimestamp } = await import('firebase/firestore');
      await updateDoc(doc(db, COLLECTIONS.people, personId), {
        notificationPrefs: prefs,
        updatedAt: serverTimestamp(),
      });
      return ok(undefined);
    } catch (cause) {
      return err(translateFirestoreError(cause, 'notification preferences'));
    }
  }

  createPerson(input: CreatePersonInput): Promise<Result<CreatedPerson, RepositoryError>> {
    return callable<CreatePersonInput, CreatedPerson>('createPerson', input);
  }

  terminatePerson(
    input: TerminatePersonInput,
  ): Promise<Result<TerminationResult, RepositoryError>> {
    return callable<TerminatePersonInput, TerminationResult>('terminatePerson', input);
  }

  reactivatePerson(
    input: ReactivatePersonInput,
  ): Promise<Result<ReactivationResult, RepositoryError>> {
    return callable<ReactivatePersonInput, ReactivationResult>('reactivatePerson', input);
  }

  activateAccount(input: ActivateAccountInput): Promise<Result<void, RepositoryError>> {
    return callable<ActivateAccountInput, void>('validatePendingAccount', input);
  }

  async pendingMatches(
    pendingId: PersonId,
  ): Promise<Result<readonly PendingAccountMatch[], RepositoryError>> {
    const result = await callable<{ pendingId: string }, { matches: unknown }>(
      'pendingAccountMatches',
      { pendingId },
    );
    if (!result.ok) return result;
    const raw = Array.isArray(result.value.matches) ? (result.value.matches as unknown[]) : [];
    const text = (record: Record<string, unknown>, key: string): string =>
      typeof record[key] === 'string' ? record[key] : '';
    return ok(
      raw.flatMap((item): PendingAccountMatch[] => {
        if (typeof item !== 'object' || item === null) return [];
        const record = item as Record<string, unknown>;
        const personId = text(record, 'personId');
        if (personId.length === 0) return [];
        return [
          {
            personId: asId<'PersonId'>(personId),
            displayName: text(record, 'displayName'),
            email: text(record, 'email'),
            areaName: text(record, 'areaName'),
            reason: text(record, 'reason'),
          },
        ];
      }),
    );
  }

  mergePendingAccount(input: MergePendingAccountInput): Promise<Result<void, RepositoryError>> {
    return callable<MergePendingAccountInput, void>('mergePendingAccount', input);
  }

  setRole(personId: PersonId, role: Role): Promise<Result<void, RepositoryError>> {
    return callable<{ personId: string; role: Role }, void>('setPersonRole', { personId, role });
  }

  setPermissions(input: SetPersonPermissionsInput): Promise<Result<void, RepositoryError>> {
    return callable<SetPersonPermissionsInput, void>('setPersonPermissions', input);
  }

  attachDocument(input: AttachPersonDocumentInput): Promise<Result<void, RepositoryError>> {
    return callable<AttachPersonDocumentInput, void>('attachPersonDocument', input);
  }

  transferArea(input: TransferAreaInput): Promise<Result<TransferAreaResult, RepositoryError>> {
    return callable<
      {
        personId: string;
        toAreaId: string;
        toSubAreaId: string | null;
        leaderPersonIds: readonly string[];
      },
      TransferAreaResult
    >('transferPersonArea', {
      personId: input.personId,
      toAreaId: input.toAreaId,
      toSubAreaId: input.toSubAreaId,
      leaderPersonIds: input.leaderPersonIds,
    });
  }

  setSubArea(
    personId: PersonId,
    subAreaId: SubAreaId | null,
  ): Promise<Result<void, RepositoryError>> {
    return callable<{ personId: string; subAreaId: string | null }, void>('setPersonSubArea', {
      personId,
      subAreaId,
    });
  }

  updateDetail(
    personId: PersonId,
    patch: PersonDetailPatch,
  ): Promise<Result<PersonDetailUpdate, RepositoryError>> {
    // Se mandan sólo las claves presentes: el servidor distingue «no lo toques» de
    // «ponlo en vacío», y enviar `undefined` explícito borraría esa diferencia.
    return callable<Record<string, unknown>, PersonDetailUpdate>('updatePersonDetail', {
      personId,
      ...patch,
    });
  }

  adjustBalance(
    personId: PersonId,
    adjustment: BalanceAdjustment,
  ): Promise<Result<BalanceAdjusted, RepositoryError>> {
    return callable<Record<string, unknown>, BalanceAdjusted>('adjustPersonBalance', {
      personId,
      ...adjustment,
    });
  }
}

/**
 * The leader junction.
 *
 * This collection is the reason the model migrates cleanly: a person-to-leader
 * relation is many-to-many, and the prototype's embedded `leads: []` array could
 * not be normalised into a table or queried from the leader's side. Here the same
 * rows answer both directions.
 */
export class FirestorePersonLeaderRepository implements IPersonLeaderRepository {
  constructor(private readonly logger: ILogger) {}

  /**
   * Quién le firma a esta persona.
   *
   * Un vacío que no se pudo confirmar se devuelve como error y no como lista
   * vacía, y es lo contrario de lo que hace el resto del repositorio a propósito.
   *
   * Cuando el canal de escucha no conecta —un proxy corporativo que lo corta, que
   * es lo que le pasa a parte del equipo—, el SDK se declara sin conexión y a
   * partir de ahí contesta las lecturas con lo que tenga en la caché local. De
   * `personLeaders`, una colección que casi ninguna pantalla lee, lo que tiene
   * guardado es nada. Esa nada volvía como una respuesta correcta de cero
   * documentos, y la ficha la escribía como «No tiene líderes asignados, así que
   * sus solicitudes no llegan a nadie» — la afirmación más alarmante de la
   * pantalla, dicha sin haber preguntado a nadie.
   *
   * Una lista vacía traída de una caché vacía no es una respuesta: es la ausencia
   * de una. Se devuelve como tal para que la pantalla pueda decir «no lo pudimos
   * confirmar», que es lo que de verdad sabe.
   */
  async leadersOf(
    personId: PersonId,
    areaId: AreaId,
  ): Promise<Result<readonly PersonLeaderLink[], RepositoryError>> {
    let fromCache = false;
    /* Los dos filtros, y el de `areaId` no es para acotar sino para que la regla
       se pueda probar. Ver `IPersonLeaderRepository.leadersOf`. */
    const links = await this.queryLinks(
      [where('personId', '==', personId), where('areaId', '==', areaId)],
      'leadersOf',
      () => {
        fromCache = true;
      },
    );

    if (links.ok && isUnconfirmedEmpty(links.value.length, fromCache)) {
      return err({
        kind: 'unavailable',
        detail: 'No pudimos consultar quién le firma: el portal está respondiendo sin conexión.',
      });
    }

    return links;
  }

  reportsOf(
    leaderPersonId: PersonId,
  ): Promise<Result<readonly PersonLeaderLink[], RepositoryError>> {
    return this.queryLinks([where('leaderPersonId', '==', leaderPersonId)], 'reportsOf');
  }

  listByArea(areaId: AreaId): Promise<Result<readonly PersonLeaderLink[], RepositoryError>> {
    return this.queryLinks([where('areaId', '==', areaId)], 'listByArea');
  }

  /**
   * `onCache` avisa si alguna página salió de la caché local en vez del servidor.
   *
   * Se pasa por closure y no se devuelve para no cambiarle la forma a
   * `collectAllPages`, que tiene once llamadores y a ninguno le hace falta.
   */
  private queryLinks(
    constraints: readonly QueryConstraint[],
    what: string,
    onCache?: () => void,
  ): Promise<Result<readonly PersonLeaderLink[], RepositoryError>> {
    return collectAllPages(async (page) => {
      const result = await runPagedQuery(
        collection(firebase().db, COLLECTIONS.personLeaders),
        constraints,
        page,
        decodePersonLeaderLink,
        what,
        (failures) => this.logger.warn('undecodable personLeader documents', { failures }),
      );
      if (result.ok && result.value.fromCache === true) onCache?.();
      return result;
    });
  }

  link(personId: PersonId, leaderPersonId: PersonId): Promise<Result<void, RepositoryError>> {
    return callable<{ linkId: string; personId: string; leaderPersonId: string }, void>(
      'assignLeader',
      { linkId: personLeaderLinkId(personId, leaderPersonId), personId, leaderPersonId },
    );
  }

  unlink(personId: PersonId, leaderPersonId: PersonId): Promise<Result<void, RepositoryError>> {
    return callable<{ linkId: string; personId: string; leaderPersonId: string }, void>(
      'removeLeader',
      { linkId: personLeaderLinkId(personId, leaderPersonId), personId, leaderPersonId },
    );
  }
}

// ---------------------------------------------------------------------------
// Holidays
// ---------------------------------------------------------------------------

export class FirestoreHolidayRepository implements IHolidayRepository {
  constructor(private readonly logger: ILogger) {}

  listForYear(year: number): Promise<Result<readonly Holiday[], RepositoryError>> {
    return collectAllPages((page) =>
      runPagedQuery(
        collection(firebase().db, COLLECTIONS.holidays),
        [
          where('companyId', '==', firebase().env.companyId),
          where('year', '==', year),
          orderBy('date'),
        ],
        page,
        decodeHoliday,
        'holidays',
        (failures) => this.logger.warn('undecodable holiday documents', { failures }),
      ),
    );
  }

  /**
   * The set of non-working dates for the business-day calculation.
   *
   * Reads the years a range spans — usually one, two when a request crosses New
   * Year. Only enabled holidays are included, which is how a day HR has switched
   * off stops being deducted immediately for everyone.
   */
  async nonWorkingDates(
    years: readonly number[],
  ): Promise<Result<ReadonlySet<string>, RepositoryError>> {
    const perYear = await Promise.all(years.map((year) => this.listForYear(year)));

    const all: Holiday[] = [];
    for (const result of perYear) {
      if (!result.ok) return err(result.error);
      all.push(...result.value);
    }
    return ok(enabledHolidayDates(all));
  }

  async add(input: AddHolidayInput): Promise<Result<HolidayId, RepositoryError>> {
    const payload = {
      date: input.date.toISO(),
      name: input.name,
      type: input.type,
    };
    const result = await callable<typeof payload, { holidayId: string }>('addHoliday', payload);
    return result.ok ? ok(result.value.holidayId as HolidayId) : err(result.error);
  }

  setEnabled(id: HolidayId, enabled: boolean): Promise<Result<void, RepositoryError>> {
    return callable<{ holidayId: string; enabled: boolean }, void>('setHolidayEnabled', {
      holidayId: id,
      enabled,
    });
  }

  remove(id: HolidayId): Promise<Result<void, RepositoryError>> {
    return callable<{ holidayId: string }, void>('removeHoliday', { holidayId: id });
  }

  async seedYear(
    year: number,
    includeCustomary: boolean,
  ): Promise<Result<number, RepositoryError>> {
    const result = await callable<{ year: number; includeCustomary: boolean }, { created: number }>(
      'seedHolidayYear',
      { year, includeCustomary },
    );
    return result.ok ? ok(result.value.created) : err(result.error);
  }
}

// ---------------------------------------------------------------------------
// Balances
// ---------------------------------------------------------------------------

/**
 * Balances are read-only from the client: a Cloud Function maintains them on every
 * request transition. Nothing authorises off these documents — the approval callable
 * recomputes from the source requests — so a counter that lags cannot grant days
 * that are not there.
 */
export class FirestoreBalanceRepository implements IBalanceRepository {
  constructor(private readonly logger: ILogger) {}

  get(personId: PersonId, periodYear: number): Promise<Result<Balance, RepositoryError>> {
    return readOne(COLLECTIONS.balances, `${personId}_${String(periodYear)}`, decodeBalance);
  }

  async getMany(refs: readonly BalanceRef[]): Promise<Result<readonly Balance[], RepositoryError>> {
    if (refs.length === 0) return ok([]);

    /* Cada quien con su periodo: el id del documento lleva el año del aniversario
       que lo abrió, y dos personas del mismo equipo pueden estar en años
       distintos el mismo día. */
    const ids = [
      ...new Set(refs.map((ref) => `${ref.personId}_${String(ref.periodYear)}`)),
    ];
    const batches = chunk(ids, IN_QUERY_CHUNK);

    const results = await Promise.all(
      batches.map((batch) =>
        runPagedQuery(
          collection(firebase().db, COLLECTIONS.balances),
          [where(documentId(), 'in', batch)],
          { limit: batch.length },
          decodeBalance,
          'balances batch',
        ),
      ),
    );

    const balances: Balance[] = [];
    for (const result of results) {
      if (!result.ok) return err(result.error);
      balances.push(...result.value.items);
    }
    return ok(balances);
  }

  /** Ordered server-side on the stored `remainingDays`, which is precisely why that
   *  derived value is persisted rather than computed on read. */
  listAtRisk(
    areaId: AreaId,
    year: number,
    threshold: number,
    page: PageRequest,
  ): Promise<Result<Page<Balance>, RepositoryError>> {
    return runPagedQuery(
      collection(firebase().db, COLLECTIONS.balances),
      [
        where('areaId', '==', areaId),
        where('year', '==', year),
        where('remainingDays', '>', threshold),
        orderBy('remainingDays', 'desc'),
      ],
      page,
      decodeBalance,
      'balances at risk',
      (failures) => this.logger.warn('undecodable balance documents', { failures }),
    );
  }
}

/** Kept exported so a future adapter can reuse the raw-document decode path. */
export const decodeRawDocument = safeDecode;
export type { QueryConstraint };
export const buildQuery = query;
